// Webrtc plugin.
import { deviceActions } from '../interface/actions'
import { forwardAction } from '../../common/utils'

// Libraries.
import { takeEvery } from 'redux-saga/effects'
import { eventChannel } from 'redux-saga'

/**
 * Listen for events emitted from the webRTC Device Manager.
 * @method watchDeviceEvents
 * @param  {Object} manager The webRTC Device Manager.
 */
export default function * watchDeviceEvents (manager) {
  /**
   * Create an event channel between the Device Manager and redux-saga.
   * This pushes its events into the redux-saga context.
   * */
  const managerChannel = eventChannel(emit => {
    return setListeners(manager, emit)
  })

  // managerChannel will emit the Manager's events as actions,
  //  so they can be directly dispatched.
  yield takeEvery(managerChannel, forwardAction)
}

/**
 * Function to set-up listeners for the Device Manager events.
 * Contains the logic for how a webRTC event should be parsed
 *    before being sent elsewhere. Split into a stand-alone
 *    function in order to be re-usable.
 * @method setListeners
 * @param  {Object}   manager The Device Manager.
 * @param  {Function} emit    Function that receives the parsed events.
 * @param  {Any}      [END='END'] The "end of lifetime" symbol.
 * @return {Function} The unsubscribe function.
 */
export function setListeners (manager, emit, END = 'END') {
  // Manager event handlers.
  const change = () => {
    // Get the latest devices after they changed, then emit the device list
    //  upwards.
    manager.checkDevices().then(devices => {
      emit(deviceActions.devicesChanged(devices))
    })
  }

  manager.on('change', change)

  // Return an unsubscribe function that removes all event listeners.
  const unsubscribe = () => {
    manager.off('change', change)
  }

  return unsubscribe
}
